<template>
  <div>
    <FilterOverview title="Daten-Upload" :showFilterInitially="false">
      <template #table="{ tableHeight }">
        <div class="daten-upload" :style="{ height: tableHeight + 87 + 'px' }">
          <!-- Umschalter zwischen den Datenformaten -->
          <RestriktionenSegmentedControl class="daten-upload__tabs" :options="tabs" v-model="activeTab" />

          <div class="daten-upload__import">
            <DateiUpload
              class="daten-upload__upload"
              :value="dateien"
              :accept-formats="['.csv']"
              :allow-multiple-files="false"
              :max-file-size-mb="25"
              hide-dropzone-with-files
              @input="onDateienChange"
            />
            <span v-if="dateien.length === 0" class="daten-upload__hinweis">
              Bitte lade die Daten als CSV-Datei hoch (max. 25 MB).
            </span>

            <!-- Erkanntes Datenformat, Validierungsfehler und der Upload, erst nach einem Upload-Versuch -->
            <div v-if="dateien.length > 0" class="daten-upload__status">
              <div class="daten-upload__status-banners">
                <div v-if="datenformatText" class="daten-upload__banner">
                  <span class="daten-upload__banner-icon"><Info :size="16" /></span>
                  <span>Datenformat: {{ datenformatText }}</span>
                </div>

                <div v-if="fileValidationError" class="daten-upload__banner daten-upload__banner--warning">
                  <span class="daten-upload__banner-icon daten-upload__banner-icon--warning">
                    <TriangleAlert :size="16" />
                  </span>
                  <span>{{ fileValidationError }}</span>
                </div>
              </div>

              <b-button
                v-if="showGrid"
                class="dialog-btn"
                variant="primary"
                :disabled="disabledUploadButton"
                @click="upload"
              >
                Upload der Daten
              </b-button>
            </div>
          </div>

          <AgGridVue
            v-if="showGrid"
            rowHeight="50px"
            class="ag-theme-alpine daten-upload__grid m-0 p-0"
            :rowData="rowData"
            :defaultColDef="defaultColDef"
            :columnDefs="columnDefs"
            :rowClassRules="rowClassRules"
            @grid-ready="onGridReady"
            :statusBar="statusBar"
            :enableCellTextSelection="true"
            :tooltipShowDelay="0"
            suppressContextMenu
            @cellValueChanged="onCellValueChanged"
          ></AgGridVue>
        </div>
      </template>
    </FilterOverview>
  </div>
</template>

<script>
import FilterOverview from '@/components/common/filter-overview.vue';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridVue } from 'ag-grid-vue';
import 'ag-grid-enterprise';
import StatusBarComponent from '@/components/flugverfuegbarkeit/status-bar-ag-grid.vue';
import CustomTooltip from '@/components/flugverfuegbarkeit/custom-tooltip.vue';
import apiService from '@/core/common/services/api.service';
import { alleLaender } from '@/core/common/iso3166Countries.js';
import DateiUpload from '@/components/common/datei-upload.vue';
import RestriktionenSegmentedControl from '@/components/restriktionen/restriktionen-segmented-control.vue';
import { Info, TriangleAlert } from 'lucide-vue';

export default {
  components: {
    FilterOverview,
    AgGridVue,
    StatusBarComponent,
    CustomTooltip,
    DateiUpload,
    RestriktionenSegmentedControl,
    Info,
    TriangleAlert,
  },
  data() {
    return {
      dateien: [],
      showGrid: false,
      disabledUploadButton: true,
      gridApi: null,
      gridColumnApi: null,
      columnDefs: [],
      rowData: [],
      statusBar: null,
      count: null,
      datenformatText: '',
      headers: [],
      activeTab: 'katalogbestellung',
      fileValidationError: '',
      defaultColDef: {
        flex: 1,
        sortable: false,
        resizable: false,
        suppressMenu: true,
        suppressMovable: true,
        tooltipComponent: 'CustomTooltip',
        tooltipValueGetter: params => {
          if (this.datenformatText !== 'TH Langstrecke / Inlandsflug') return undefined;
          const reise = params.data?.Reise;
          const startdatum = params.data?.Startdatum;
          if (!reise || !startdatum) return undefined;
          const key = `${reise}_${startdatum}`;
          if (this.nichtGefundeneReisetermine.has(key)) {
            return 'Reisetermin nicht gefunden - bitte prüfen';
          }
          if (this.verworfeneReisetermine.has(key)) {
            return 'Termin verworfen, bitte prüfen.';
          }
          return undefined;
        },
      },
      katalogbestellungHeaders: [
        {
          key: 'gender',
          value: 'Anrede',
        },
        { key: 'given_name', value: 'Vorname' },
        { key: 'family_name', value: 'Nachname' },
        { key: 'street_address', value: 'Straße und Hausnummer' },
        { key: 'postal_code', value: 'PLZ' },
        { key: 'address_locality', value: 'Ort' },
        { key: 'address_country', value: 'Land' },
        { key: 'email_address', value: 'E-Mail Adresse' },
        { key: 'catalogues', value: 'Katalog' },
      ],
      forecastPaxHeaders: [
        { key: 'reisetermin', value: 'Reisetermin' },
        { key: 'FC_Pax', value: 'FC Pax' },
      ],
      planPaxHeaders: [
        { key: 'reisetermin', value: 'Reisetermin' },
        { key: 'Plan_Pax', value: 'Plan Pax' },
      ],
      thLangstreckeInlandsflugHeader: [
        { key: 'Reise', value: 'Reise' },
        { key: 'Startdatum', value: 'Startdatum' },
        { key: 'TH_Langstrecke', value: 'TH Langstrecke' },
        { key: 'TH_Inlandsflug', value: 'TH Inlandsflug' },
      ],
      verworfeneReisetermine: new Set(),
      nichtGefundeneReisetermine: new Set(),
      rowClassRules: {
        'red-address': row => {
          return this.isDataInvalid(row.data);
        },
        'row-verworfen': row => {
          if (this.datenformatText !== 'TH Langstrecke / Inlandsflug') return false;
          const reise = row.data?.Reise;
          const startdatum = row.data?.Startdatum;
          if (!reise || !startdatum) return false;
          return this.verworfeneReisetermine.has(`${reise}_${startdatum}`);
        },
        'row-nicht-gefunden': row => {
          if (this.datenformatText !== 'TH Langstrecke / Inlandsflug') return false;
          const reise = row.data?.Reise;
          const startdatum = row.data?.Startdatum;
          if (!reise || !startdatum) return false;
          return this.nichtGefundeneReisetermine.has(`${reise}_${startdatum}`);
        },
      },
      genderMapping: {
        MALE: 'Herr',
        FEMALE: 'Frau',
        DIVERSE: 'Divers',
        m: 'Herr',
        w: 'Frau',
        d: 'Divers',
      },
    };
  },
  computed: {
    tabs() {
      return [
        { text: 'Katalogbestellung', value: 'katalogbestellung' },
        { text: 'Forecast Pax', value: 'forecastPax' },
        { text: 'TH Langstrecke / Inlandsflug', value: 'thLangstreckeInlandsflug' },
      ];
    },
  },
  created() {
    this.statusBar = {
      statusPanels: [{ statusPanel: 'StatusBarComponent', key: 'statusBarCompKey', align: 'left' }],
    };
  },
  watch: {
    rowData: {
      deep: true,
      handler() {
        this.checkRedRows();
      },
    },
    activeTab() {
      this.zuruecksetzen();
    },
  },
  methods: {
    parseZahlenformat(value) {
      // "120,00" -> 120.00, "120,20" -> 120.20
      if (typeof value === 'string') {
        return parseFloat(value.replace(',', '.'));
      }
      return parseFloat(value);
    },

    isValidCountryCode(countryCode) {
      // Prüft ob der Code genau 2 Zeichen hat und ein gültiger ISO-Code ist
      const laender = alleLaender();
      if (!countryCode || countryCode.length !== 2) {
        return false;
      }
      return (
        !Object.keys(laender).length ||
        Object.prototype.hasOwnProperty.call(laender, countryCode.toUpperCase())
      );
    },

    isValidReisetermin(reisetermin) {
      if (!reisetermin) return false;

      const reiseterminPattern = /^[A-Za-z]{6}_\d{4}-\d{2}-\d{2}$/;
      return reiseterminPattern.test(reisetermin);
    },

    validateCountryData(data) {
      const invalidCountries = [];
      data.forEach((row, index) => {
        const countryCode = row.address_country;
        if (countryCode && !this.isValidCountryCode(countryCode)) {
          invalidCountries.push({
            row: index + 1,
            value: countryCode,
            message: `Ungültiger Ländercode: "${countryCode}"`,
          });
        }
      });

      return invalidCountries;
    },

    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;

      this.updateStatusBar(this.count);
    },

    onDateienChange(files) {
      this.dateien = files;
      if (files.length === 0) {
        this.zuruecksetzen();
        return;
      }
      this.handleFile(files[0]);
    },

    // Setzt die Ansicht auf den Zustand ohne ausgewählte Datei zurück
    zuruecksetzen() {
      this.dateien = [];
      this.fileValidationError = '';
      this.datenformatText = '';
      this.showGrid = false;
      this.columnDefs = [];
      this.rowData = [];
      this.count = null;
      this.disabledUploadButton = true;
      // Das Grid wird mit showGrid ausgebaut, die alte API darf nicht weiterverwendet werden
      this.gridApi = null;
      this.gridColumnApi = null;
      this.verworfeneReisetermine = new Set();
      this.nichtGefundeneReisetermine = new Set();
    },

    handleFile(file) {
      if (!file) return;

      this.fileValidationError = '';
      this.datenformatText = '';

      const reader = new FileReader();
      reader.onload = e => {
        const content = e.target.result;
        const fileData = this.parseCSV(content);

        if (!this.validateFileForCurrentTab(fileData)) {
          this.showGrid = false;
          return;
        }

        this.importDataToGrid(fileData);
      };
      reader.readAsText(file);
    },

    validateFileForCurrentTab(fileData) {
      if (!fileData || fileData.length === 0) return false;

      const csvHeaders = fileData[0];
      const headerInfo = this.richtigeHeaderAuswaehlen(csvHeaders);

      if (this.activeTab === 'katalogbestellung') {
        if (headerInfo.type !== 'Katalogbestellung') {
          this.fileValidationError =
            'Für die Katalogbestellung sind nur CSV-Dateien mit Katalogbestellungsdaten erlaubt.';
          return false;
        }
      } else if (this.activeTab === 'forecastPax') {
        if (headerInfo.type !== 'Forecast Pax' && headerInfo.type !== 'Plan Pax') {
          this.fileValidationError =
            'Für die Forecast Pax sind nur CSV-Dateien mit Pax-Daten (Forecast oder Plan) erlaubt.';
          return false;
        }
      } else if (this.activeTab === 'thLangstreckeInlandsflug') {
        if (headerInfo.type !== 'TH Langstrecke / Inlandsflug') {
          this.fileValidationError =
            'Für die TH Langstrecke / Inlandsflug sind nur CSV-Dateien mit TH Langstrecke- / Inlandsflug-daten erlaubt.';
          return false;
        }
      }
      return true;
    },

    parseCSV(content) {
      const separator = content.includes(';') ? ';' : ',';
      const rows = content.split('\n').filter(row => row.trim() !== '');
      return rows.map(row => row.split(separator).map(cell => cell.trim()));
    },

    richtigeHeaderAuswaehlen(csvHeaders) {
      const isKatalogbestellung = this.katalogbestellungHeaders.every(header =>
        csvHeaders.includes(header.key)
      );
      if (isKatalogbestellung) {
        return {
          type: 'Katalogbestellung',
          headers: this.katalogbestellungHeaders,
        };
      }

      const isForecastPax = this.forecastPaxHeaders.every(header => csvHeaders.includes(header.key));
      if (isForecastPax) {
        return {
          type: 'Forecast Pax',
          headers: this.forecastPaxHeaders,
        };
      }

      const isPlanPax = this.planPaxHeaders.every(header => csvHeaders.includes(header.key));
      if (isPlanPax) {
        return {
          type: 'Plan Pax',
          headers: this.planPaxHeaders,
        };
      }

      const isThLangstreckeInlandsFlug = this.thLangstreckeInlandsflugHeader.every(header =>
        csvHeaders.includes(header.key)
      );
      if (isThLangstreckeInlandsFlug) {
        return {
          type: 'TH Langstrecke / Inlandsflug',
          headers: this.thLangstreckeInlandsflugHeader,
        };
      }

      return {
        type: 'Unbekanntes Datenformat',
        headers: [],
      };
    },

    importDataToGrid(fileData) {
      if (!fileData || fileData.length === 0) return;

      const csvHeaders = fileData[0];
      const headerInfo = this.richtigeHeaderAuswaehlen(csvHeaders);

      this.datenformatText = headerInfo.type;

      if (headerInfo.type !== 'Unbekanntes Datenformat') {
        this.showGrid = true;
        const columns = this.getColumnDefs(headerInfo.headers);
        this.columnDefs = columns;

        // header werden "abgeschnitten"
        const gridData = fileData.slice(1);
        this.rowData = this.dataAnordnen(gridData, csvHeaders, headerInfo.headers);

        // Status-Check für TH Langstrecke / Inlandsflug (verworfen + nicht gefunden)
        if (headerInfo.type === 'TH Langstrecke / Inlandsflug') {
          this.checkReisetermineStatus();
        }

        // Ländervalidierung für Katalogbestellung
        if (headerInfo.type === 'Katalogbestellung') {
          const validationErrors = this.validateCountryData(this.rowData);
          if (validationErrors.length > 0) {
            console.log('Ungültige Ländercodes gefunden:', validationErrors);
            this.fileValidationError = `${validationErrors.length} ungültige Ländercodes gefunden`;
          }
        }

        this.count = this.rowData.length;

        // Beim ersten Import existiert das Grid noch nicht, die rowData-Bindung füllt es dann selbst
        if (this.gridApi) {
          this.gridApi.setRowData(this.rowData);
          this.updateStatusBar(this.rowData.length);
        }
      } else {
        this.columnDefs = [];
        this.rowData = [];
      }
    },

    async checkReisetermineStatus() {
      const kuerzel = this.rowData
        .map(row => {
          const reise = row['Reise'];
          const startdatum = row['Startdatum'];
          return reise && startdatum ? `${reise}_${startdatum}` : null;
        })
        .filter(k => k !== null);

      if (kuerzel.length === 0) {
        this.verworfeneReisetermine = new Set();
        this.nichtGefundeneReisetermine = new Set();
        return;
      }

      const kuerzelFilter = kuerzel.map(k => `'${k}'`).join(',');
      const filter = `reiseterminkuerzel in (${kuerzelFilter})`;

      try {
        const res = await apiService.get(`Reisetermin?$filter=${filter}&$select=reiseterminkuerzel,state`);
        const found = res.data?.result?.value || [];
        const foundKuerzel = new Set(found.map(rt => rt.reiseterminkuerzel));

        this.verworfeneReisetermine = new Set(
          found.filter(rt => rt.state === 'Verworfen').map(rt => rt.reiseterminkuerzel)
        );
        this.nichtGefundeneReisetermine = new Set(kuerzel.filter(k => !foundKuerzel.has(k)));
      } catch (err) {
        console.error('Fehler beim Prüfen der Reisetermin-States:', err);
      }

      if (this.gridApi) {
        this.gridApi.redrawRows();
      }
      this.checkRedRows();
    },

    getColumnDefs(headers) {
      const deleteColumn = {
        headerName: '',
        field: 'deleteButton',
        width: 50,
        pinned: 'left',
        cellRenderer: params => {
          if (this.isDataInvalid(params.data)) {
            const button = document.createElement('button');
            button.innerHTML = '<i class="fas fa-times"></i>';
            button.className = 'btn btn-sm';
            button.addEventListener('click', () => this.removeRow(params.data));
            return button;
          }
          return '';
        },
        cellStyle: {
          display: 'flex',
          'align-items': 'center',
          'justify-content': 'center',
        },
      };
      const dataColumns = headers.map(header => {
        const column = {
          headerName: header.value,
          field: header.key,
          editable: true,
        };

        if (header.key === 'gender') {
          column.valueFormatter = params => {
            return this.genderMapping[params.value];
          };
        }

        return column;
      });

      if (this.datenformatText === 'Forecast Pax') {
        console.log('fc datenformat');
        return [...dataColumns];
      } else if (this.datenformatText === 'Plan Pax') {
        console.log('plan datenformat');
        return [...dataColumns];
      } else if (this.datenformatText === 'TH Langstrecke / Inlandsflug') {
        console.log('TH werte datenformat');
        return [...dataColumns];
      } else {
        return [deleteColumn, ...dataColumns];
      }
    },

    dataAnordnen(gridData, csvHeaders, benoetigteHeader) {
      const mappedGrid = gridData
        .map(row => {
          // für jede row wird ein neues Objekt erstellt
          const rowObject = {};
          benoetigteHeader.forEach(header => {
            // hier werden die spalten den benötigten Headern zugeordnet
            const columnIndex = csvHeaders.indexOf(header.key);
            if (columnIndex >= 0 && columnIndex < row.length) {
              rowObject[header.key] = row[columnIndex];
            } else {
              rowObject[header.key] = '';
            }
          });
          return rowObject;
        })
        // filtert leere Rows direkt raus
        .filter(row => benoetigteHeader.some(header => row[header.key] && row[header.key].trim() !== ''));
      let compareFn = (a, b) => {
        const sortOrderA = this.isDataInvalid(a) ? 0 : 1;
        const sortOrderB = this.isDataInvalid(b) ? 0 : 1;
        return sortOrderA - sortOrderB;
      };
      const sortedGrid = mappedGrid.sort(compareFn);

      return sortedGrid;
    },

    updateStatusBar(displayedRowsCount) {
      if (!this.gridApi) return;
      const statusBarComponent = this.gridApi?.getStatusPanel('statusBarCompKey');

      if (statusBarComponent) {
        statusBarComponent.setRowCount(displayedRowsCount);
        statusBarComponent.setOdataCount(this.count);
      }
    },

    async upload() {
      if (this.datenformatText === 'Forecast Pax') {
        const updateValues = this.rowData.reduce((acc, curr) => {
          const reisetermin = curr['reisetermin'];
          const forecastPax = parseInt(curr['FC_Pax']);
          if (reisetermin && !isNaN(forecastPax)) {
            acc[reisetermin] = forecastPax;
          }
          return acc;
        }, {});

        apiService.put('Reisetermin/bulkUpdateForecastPax', { updateValues }).then(res => {
          console.log(res);
          this.toast('FC Pax gespeichert!');
        });
      } else if (this.datenformatText === 'Plan Pax') {
        const updateValues = this.rowData.reduce((acc, curr) => {
          const reisetermin = curr['reisetermin'];
          const PlanPax = parseInt(curr['Plan_Pax']);
          if (reisetermin && !isNaN(PlanPax)) {
            acc[reisetermin] = PlanPax;
          }
          return acc;
        }, {});

        apiService.put('Reisetermin/bulkUpdatePlanPax', { updateValues }).then(res => {
          console.log(res);
          this.toast('Plan Pax gespeichert!');
        });
      } else if (this.datenformatText === 'Katalogbestellung') {
        const katalogData = this.transformKatalogData(this.rowData);
        apiService
          .post(
            'https://api.skr.de/zoho-add-katalogbestellung?code=baJzwQrnm3LfcuAEp65ge_F3VNfajbZ8IWorWhWNCpBkAzFuXADnFg%3D%3D',
            katalogData
          )
          .then(res => {
            console.log(res);
            this.toast('Katalogbestellung wurde hochgeladen');
          });
      } else if (this.datenformatText === 'TH Langstrecke / Inlandsflug') {
        const updateValues = this.rowData.reduce((acc, curr) => {
          const reise = curr['Reise'];
          const startdatum = curr['Startdatum'];
          const reisetermin = reise && startdatum ? `${reise}_${startdatum}` : null;
          const thLangstrecke = this.parseZahlenformat(curr['TH_Langstrecke']);
          const thInlandsflug = this.parseZahlenformat(curr['TH_Inlandsflug']);

          if (reisetermin && !isNaN(thLangstrecke)) {
            acc[reisetermin] = {
              thLangstrecke: thLangstrecke,
            };

            // TH_Inlandsflug nur hinzufügen wenn nicht leer/NaN
            if (!isNaN(thInlandsflug)) {
              acc[reisetermin].thInlandsflug = thInlandsflug;
            }
          }
          return acc;
        }, {});
        console.log('updateValues: ', updateValues);

        apiService.put('Reisetermin/bulkUpdateThWerte', { updateValues }).then(res => {
          console.log('Response:', res);
          this.toast('TH-Werte gespeichert!');
        });
      }

      this.zuruecksetzen();
    },

    isDataInvalid(row) {
      if (!row) {
        return false;
      }

      // Validierung für Erwartete Pax (Forecast und Plan)
      if (this.datenformatText === 'Forecast Pax' || this.datenformatText === 'Plan Pax') {
        return !this.isValidReisetermin(row.reisetermin);
      }

      // Validierung für TH Langstrecke / Inlandsflug
      if (this.datenformatText === 'TH Langstrecke / Inlandsflug') {
        // Reise: 6 Buchstaben
        const reisePattern = /^[A-Za-z]{6}$/;
        if (!row.Reise || !reisePattern.test(row.Reise)) return true;

        // Startdatum: yyyy-mm-dd Format
        const datumPattern = /^\d{4}-\d{2}-\d{2}$/;
        if (!row.Startdatum || !datumPattern.test(row.Startdatum)) return true;

        const thLangstrecke = this.parseZahlenformat(row['TH_Langstrecke']);
        const thInlandsflug = this.parseZahlenformat(row['TH_Inlandsflug']);

        // TH_Langstrecke: Pflichtfeld
        if (isNaN(thLangstrecke) || thLangstrecke < 0 || !Number.isInteger(thLangstrecke)) {
          return true;
        }

        // TH_Inlandsflug: Optional
        if (!isNaN(thInlandsflug) && (thInlandsflug < 0 || !Number.isInteger(thInlandsflug))) {
          return true;
        }

        return false;
      }

      if (this.datenformatText === 'Katalogbestellung') {
        const requiredFields = [
          'gender',
          'given_name',
          'family_name',
          'street_address',
          'postal_code',
          'address_locality',
          'address_country',
          'catalogues',
        ];

        const isMissingRequiredField = requiredFields.some(field => !row[field]);

        const isEmailFieldMissing = !row.hasOwnProperty('email_address');

        if (isMissingRequiredField || isEmailFieldMissing) return true;

        const isGenderInvalid =
          row.gender &&
          !Object.keys(this.genderMapping).includes(row.gender) &&
          !Object.values(this.genderMapping).includes(row.gender);

        const isAddressInvalid = row.street_address.length > 25;
        const isGivenNameInvalid = row.given_name.length > 20;
        const isFamilyNameInvalid = row.family_name.length > 20;
        const isPostalCodeInvalid = row.postal_code.length > 8;
        const isAddressLocalityInvalid = row.address_locality.length > 20;
        const isCountryInvalid =
          row.address_country.length > 2 || !this.isValidCountryCode(row.address_country);

        // E-Mail-Validierung: Wenn E-Mail vorhanden, muss sie ein @ enthalten
        const isEmailInvalid =
          row.email_address && row.email_address.trim() !== '' && !row.email_address.includes('@');

        const hasRequiredCatalogue = row.catalogues
          .split(',')
          .map(catalogue => catalogue.trim())
          .some(catalogue => {
            const fernMatch = catalogue.match(/Fern_(\d{4})/);
            const europaMatch = catalogue.match(/Europa_(\d{4})/);

            return (
              // schaut, ob die 4-stellige Zahl >= 2025 ist
              (fernMatch && parseInt(fernMatch[1]) >= 2025) ||
              (europaMatch && parseInt(europaMatch[1]) >= 2025)
            );
          });

        return (
          isGenderInvalid ||
          isAddressInvalid ||
          !hasRequiredCatalogue ||
          isGivenNameInvalid ||
          isFamilyNameInvalid ||
          isPostalCodeInvalid ||
          isAddressLocalityInvalid ||
          isCountryInvalid ||
          isEmailInvalid
        );
      }

      return false;
    },

    checkRedRows() {
      if (!this.rowData || this.rowData.length === 0) return;

      const hasInvalidRows = this.rowData.some(data => this.isDataInvalid(data));
      const hasVerworfeneRows = this.rowData.some(row => {
        const reise = row['Reise'];
        const startdatum = row['Startdatum'];
        return reise && startdatum && this.verworfeneReisetermine.has(`${reise}_${startdatum}`);
      });
      const hasNichtGefundeneRows = this.rowData.some(row => {
        const reise = row['Reise'];
        const startdatum = row['Startdatum'];
        return reise && startdatum && this.nichtGefundeneReisetermine.has(`${reise}_${startdatum}`);
      });
      this.disabledUploadButton = hasInvalidRows || hasVerworfeneRows || hasNichtGefundeneRows;
    },

    removeRow(row) {
      if (this.gridApi) {
        this.gridApi.applyTransaction({ remove: [row] });
        // Update counts after removal
        this.count = this.gridApi.getDisplayedRowCount();
        // das wird benötigt, damit der button sich auch färbt
        this.rowData = this.gridApi.getModel().rowsToDisplay.map(row => row.data);

        this.updateStatusBar(this.count);
      }
    },

    onCellValueChanged(params) {
      // manuell gelöschte zeilen werden auch komplett entfernt
      const rowData = params.data;
      const allEmpty = Object.values(rowData).every(
        val => !val || (typeof val === 'string' && val.trim() === '')
      );
      if (allEmpty) {
        this.removeRow(rowData);
        return;
      }

      params.api.refreshCells({
        rowNodes: [params.node],
        columns: ['deleteButton'],
        force: true,
      });

      // Bei manueller Änderung von Reise oder Startdatum erneut prüfen, ob der Reisetermin existiert
      if (
        this.datenformatText === 'TH Langstrecke / Inlandsflug' &&
        (params.colDef.field === 'Reise' || params.colDef.field === 'Startdatum')
      ) {
        this.checkReisetermineStatus();
      }
    },

    transformKatalogData(katalogData) {
      return katalogData.map(item => {
        const transformedItem = {
          zipcode: item.postal_code,
          firstName: item.given_name,
          lastName: item.family_name,
          country: item.address_country,
          city: item.address_locality,
          street: item.street_address,
          salutation: this.genderMapping[item.gender] || item.gender,
          selectedCatalogues: item.catalogues
            ? item.catalogues.split(',').map(catalogue => catalogue.trim())
            : [],
          newsletterSubscribe: true,
          quelle: 'Liste',
        };
        // E-Mail nur hinzufügen wenn nicht leer
        if (item.email_address && item.email_address.trim() !== '') {
          transformedItem.eMail = item.email_address.trim();
        }
        return transformedItem;
      });
    },
  },
};
</script>
<style scoped>
.daten-upload {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
}

/* Dropzone, Datei-Chip, Hinweis und Statuszeile bilden einen Block */
.daten-upload__import {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Statuszeile: Banner links, Upload-Aktion rechts */
.daten-upload__status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.daten-upload__status-banners {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}

.daten-upload__hinweis {
  font-size: 12px;
  line-height: 16px;
  color: var(--text-muted);
}

.daten-upload__banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: var(--radius-sm);
  background-color: #3699ff26;
  border: 1px solid var(--primary);
  font-size: 14px;
  line-height: 20px;
  color: var(--text-body);
}

.daten-upload__banner--warning {
  background-color: #ffa80026;
  border-color: var(--warning);
  color: #d97706;
}

.daten-upload__banner-icon {
  display: inline-flex;
  align-items: center;
  color: var(--primary);
}

.daten-upload__banner-icon--warning {
  color: #d97706;
}

/* Platz für das ✕, das in der Dateiliste absolut rechts außerhalb der Zeile liegt */
.daten-upload__upload ::v-deep .datei-upload-files {
  padding-right: 22px;
}

.daten-upload__grid {
  flex: 1;
  min-height: 0;
}
</style>

<style>
/* AG Grid erzeugt die Zeilen per JS, deshalb ungescopt */
.red-address {
  background-color: #f64e6026 !important;
}
.row-verworfen {
  background-color: #f64e6026 !important;
}
.row-nicht-gefunden {
  background-color: #f64e6026 !important;
}
</style>
